import moment from 'moment';

import { createAnalyticsUrl, restGet, restPost } from '~/src/dash/api/rest';
import {
  defaultTargetingPreset,
  ITargetingPreset,
  TargetingPresetKey,
  TargetingPresetKeys,
} from '~/src/dash/apiV2/targeting/targetingPresets';
import { EPodSequenceTargeting, EPositionTargeting } from '~/src/dash/utils/options';
import {
  IForecastingCards,
  IForecastingData,
  IForecastingResponse,
} from '~/src/dash/views/tools/Forecasting/common';
import {
  defaultDaypartingUI,
  IDaypartingUI,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/DaypartingBuilder/common';
import { cloneDeep } from 'lodash';

export enum EFreqCapTimePeriod {
  Unknown = 0,
  Days,
  Hours,
}

export enum EGroupByType {
  Unknown = 0,
  Tier,
  Site,
}

export type InventoryParametersKey =
  // INVENTORY
  | 'SiteIDs'
  | 'ChannelGroupIDs'
  | 'Slots'
  | 'Position'
  | 'PodSequence'
  | 'PodDurationMin'
  | 'PodDurationMax'
  | 'TargetingAllowedCities'
  | 'TargetingAllowedCountries'
  | 'TargetingAllowedDmas'
  | 'TargetingAllowedStates'
  | 'TargetingAllowedZips'
  | 'TargetingDisallowedCities'
  | 'TargetingDisallowedCountries'
  | 'TargetingDisallowedDmas'
  | 'TargetingDisallowedStates'
  | 'TargetingDisallowedZips'
  | TargetingPresetKey;

// IF YOU UPDATE THE INTERFACE BELOW, MAKE SURE TO UPDATE THE DEFAULT OBJECT ALSO
// INVENTORY PARAMETERS
export interface IInventoryParameters extends ITargetingPreset {
  // INVENTORY
  SiteIDs: string[];
  ChannelGroupIDs: number[];
  Slots: string;
  Position: EPositionTargeting;
  PodSequence: EPodSequenceTargeting;
  PodDurationMin: number;
  PodDurationMax: number;
  TargetingAllowedCities: string[];
  TargetingAllowedCountries: string[];
  TargetingAllowedDmas: string[];
  TargetingAllowedStates: string[];
  TargetingAllowedZips: string[];
  TargetingDisallowedCities: string[];
  TargetingDisallowedCountries: string[];
  TargetingDisallowedDmas: string[];
  TargetingDisallowedStates: string[];
  TargetingDisallowedZips: string[];
}

// IF YOU UPDATE THE INTERFACE BELOW, MAKE SURE TO UPDATE THE DEFAULT OBJECT ALSO
// DELIVERY VARIABLES
export interface IDeliveryVariables {
  // Time Range Adjustment
  ForecastedStartDate: moment.Moment;
  ForecastedEndDate: moment.Moment;
  // Frequency Capping
  FreqCapActive: boolean;
  FreqCapImps: number;
  FreqCapTimePeriodCount: number;
  FreqCapTimePeriod: EFreqCapTimePeriod;
  // Campaign Exclusions
  ExcludeHouseCampaigns: boolean;
  // Adjustment to Traffic
  TrafficAdjustment: number;
}

export interface IFormattedDeliveryVariables {
  // Time Range Adjustment
  ForecastedStartDate: string;
  ForecastedEndDate: string;
  // Frequency Capping
  FreqCapActive: boolean;
  FreqCapImps: number;
  FreqCapTimePeriodCount: number;
  FreqCapTimePeriod: EFreqCapTimePeriod;
  // Adjustment to Traffic
  TrafficAdjustment: number;
}

interface IForecastingConfig {
  InventoryParameters: IInventoryParameters;
  DeliveryVariables: IFormattedDeliveryVariables;
  GroupByType: EGroupByType;
}

export const InventoryParametersKeys: Record<string, InventoryParametersKey> = {
  // INVENTORY
  SITE_IDS: 'SiteIDs',
  CHANNEL_GROUP_IDS: 'ChannelGroupIDs',
  SLOTS: 'Slots',
  POSITION: 'Position',
  POD_SEQUENCE: 'PodSequence',
  POD_DURATION_MIN: 'PodDurationMin',
  POD_DURATION_MAX: 'PodDurationMax',
  TARGETING_ALLOWED_CITIES: 'TargetingAllowedCities',
  TARGETING_ALLOWED_COUNTRIES: 'TargetingAllowedCountries',
  TARGETING_ALLOWED_DMAS: 'TargetingAllowedDmas',
  TARGETING_ALLOWED_STATES: 'TargetingAllowedStates',
  TARGETING_ALLOWED_ZIPS: 'TargetingAllowedZips',
  TARGETING_DISALLOWED_CITIES: 'TargetingDisallowedCities',
  TARGETING_DISALLOWED_COUNTRIES: 'TargetingDisallowedCountries',
  TARGETING_DISALLOWED_DMAS: 'TargetingDisallowedDmas',
  TARGETING_DISALLOWED_STATES: 'TargetingDisallowedStates',
  TARGETING_DISALLOWED_ZIPS: 'TargetingDisallowedZips',
  ...TargetingPresetKeys,
};

export const defaultInventoryParameters: Partial<IInventoryParameters> = {
  // INVENTORY
  SiteIDs: [],
  ChannelGroupIDs: [],
  Slots: '',
  Position: EPositionTargeting.ALL,
  PodSequence: EPodSequenceTargeting.ANY,
  PodDurationMin: 0,
  PodDurationMax: 1000,
  TargetingAllowedCities: [],
  TargetingAllowedCountries: [],
  TargetingAllowedDmas: [],
  TargetingAllowedStates: [],
  TargetingAllowedZips: [],
  TargetingDisallowedCities: [],
  TargetingDisallowedCountries: [],
  TargetingDisallowedDmas: [],
  TargetingDisallowedStates: [],
  TargetingDisallowedZips: [],
  ...defaultTargetingPreset,
};

export function getDefaultInventoryParameters(daypartingTimezone: string): IInventoryParameters {
  return {
    ...cloneDeep(defaultInventoryParameters),
    DaypartingUI: {
      ...cloneDeep(defaultDaypartingUI),
      Timezone: daypartingTimezone,
    } as IDaypartingUI,
  } as IInventoryParameters;
}

export const defaultDeliveryVariables: IDeliveryVariables = {
  // Time Range Adjustment
  ForecastedStartDate: moment().startOf('day'),
  ForecastedEndDate: moment().add(6, 'd').endOf('day'),
  // Frequency Capping
  FreqCapActive: false,
  FreqCapImps: 0,
  FreqCapTimePeriodCount: 0,
  FreqCapTimePeriod: EFreqCapTimePeriod.Days,
  // Campaign Exclusions
  ExcludeHouseCampaigns: false,
  // Adjustment to Traffic
  TrafficAdjustment: 0,
};

export const getDefaultDeliveryVariables = (timezone: string): IDeliveryVariables => {
  const deliveryVariables = { ...defaultDeliveryVariables } as IDeliveryVariables;
  deliveryVariables.ForecastedStartDate = moment().tz(timezone).startOf('day');
  deliveryVariables.ForecastedEndDate = moment().tz(timezone).add(6, 'd').endOf('day');
  return deliveryVariables;
};

export const defaultForecastingData: IForecastingResponse = {
  GeneratedAt: '',
  ForecastingCards: {} as IForecastingCards,
  ForecastingData: [],
  ForecastingDataSummed: {} as IForecastingData,
  OverlappingLineItems: [],
};

export interface IForecastResponse {
  // users
  UniqueUserCount: number;
  PublisherUniqueUserCount: number;
  // others
  EndptReq: number;
  Imps: number;
  Revenue: number;
  CPM: number;
}

// getActiveInventoryParameters takes the inventoryParameters object and returns an array of all active parameters selected
export const getActiveInventoryParameters = (
  inventoryParameters: IInventoryParameters,
): string[] => {
  const activeInventoryParametersList = [] as InventoryParametersKey[];
  Object.entries(inventoryParameters.ActiveParameters as Record<string, string[]>).forEach(
    ([_, activeParameters]) => {
      activeParameters.forEach((activeParameter: InventoryParametersKey) => {
        activeInventoryParametersList.push(activeParameter);
      });
    },
  );
  return activeInventoryParametersList;
};

const formatDeliveryVariables = (
  deliveryVariables: IDeliveryVariables,
  timezone: string,
): IFormattedDeliveryVariables => {
  return {
    ...deliveryVariables,
    ForecastedStartDate: deliveryVariables.ForecastedStartDate.tz(timezone).format(),
    ForecastedEndDate: deliveryVariables.ForecastedEndDate.tz(timezone).format(),
    TrafficAdjustment: deliveryVariables.TrafficAdjustment / 100,
  };
};

export const validateForecast = (deliveryVariables: IDeliveryVariables): string => {
  // Delivery Variables
  if (deliveryVariables.FreqCapActive) {
    if (deliveryVariables.FreqCapImps < 1) {
      return 'Please provide a valid frequency cap impression limit.';
    }
    if (
      deliveryVariables.FreqCapTimePeriodCount < 1 ||
      deliveryVariables.FreqCapTimePeriodCount > 30
    ) {
      return 'Please provide a frequency cap period count between 1 and 30.';
    }
  }

  return '';
};

interface CanFetchForecastingDataResponse {
  success?: boolean;
}

export async function canFetchForecastingData(publisherID: string): Promise<boolean> {
  const reqUrl = `${createAnalyticsUrl('discovery/can_forecast_data', publisherID)}`;
  try {
    const response = await restGet<CanFetchForecastingDataResponse>(reqUrl);
    const parsedBody = response.parsedBody;

    // Return true if response has success key, false if it has error key
    return !!parsedBody?.success;
  } catch (err) {
    return false;
  }
}

export async function fetchForecastingData(
  publisherID: string,
  inventoryParameters: IInventoryParameters,
  deliveryVariables: IDeliveryVariables,
  groupByType: EGroupByType,
  timezone: string,
): Promise<IForecastingResponse> {
  const reqUrl =
    `${createAnalyticsUrl('discovery/forecasting_data', publisherID)}` +
    '&return_fake_data=fake_data';
  try {
    const formattedDeliveryVariables = formatDeliveryVariables(deliveryVariables, timezone);

    const forecastingConfig = {
      InventoryParameters: inventoryParameters,
      DeliveryVariables: formattedDeliveryVariables,
      GroupByType: groupByType,
    } as IForecastingConfig;

    const response = await restPost<any>(reqUrl, forecastingConfig);
    const { parsedBody } = response;

    return {
      GeneratedAt: parsedBody.GeneratedAt,
      ForecastingCards: parsedBody.ForecastingCards,
      ForecastingData:
        (parsedBody as IForecastingResponse).ForecastingData?.sort(
          (a: IForecastingData, b: IForecastingData) => b.Avails - a.Avails,
        ) || [],
      ForecastingDataSummed: parsedBody.ForecastingDataSummed,
      OverlappingLineItems: parsedBody.OverlappingLineItems,
    };
  } catch (err) {
    throw err;
  }
}
